import AddIcon from '@mui/icons-material/Add';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinTwoToneIcon from '@mui/icons-material/PushPinTwoTone';
import RemoveIcon from '@mui/icons-material/Remove';
import { Backdrop, Fab, Grid, IconButton, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { LoadError, SpecialZoomLevel, Viewer } from '@react-pdf-viewer/core';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { RenderZoomOutProps, zoomPlugin } from '@react-pdf-viewer/zoom';
import classNames from 'classnames';
import { basename, dirname, join } from 'path';
import { useEffect, useState } from 'react';
import awsConfig from '../../aws-exports';
import { s3Download, s3DownloadExternalBucket } from '../../helpers/s3';
import { scrollbarStyle } from '../../shared/dashboard';

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    "& .rpv-core__inner-pages": scrollbarStyle
  },
  fileNameContainer: {
    display: "flex",
    flexDirection: "row",
    margin: 5,
  },
  fileContainer: {
    height: '96%',
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  fileNameElement: {
    margin: "0 5px",
  },
  buttonsExcel: {
    fontSize: "0.5rem",
    margin: "0 5px",
    borderRadius: 5,
    width: 90,
    backgroundColor: "#84D0D1",
    color: 'black',
  },
  buttonsExcelActive: {
    fontSize: "0.5rem",
    margin: "0 5px",
    borderRadius: 5,
    width: 90,
    "&:hover": {
      backgroundColor: "#1797A6",
      color: 'white',
    },
    backgroundColor: 'white',
    color: theme.palette.primary.contrastText,
  },
  commentsArea: {
    height: '100%',
    overflowY: 'hidden',
    display: 'flex',
  },
  commentsLoader: {
    margin: 'auto',
  },
  filePreviewContainer: {
    height: 'inherit',
  },
  iconButton: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
    height: 56,
    width: 56,
    marginBottom: 10,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.common.white,
    },
  },
  zoomButton: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    height: 40,
    width: 40,
    marginBottom: 10,
  },
  zoomButtonsContainer: {
    position: "absolute",
    top: 25,
    right: 15,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  openIcon: {
    position: 'absolute',
    bottom: 0,
    left: 0
  },
  topLoadButton: {
    position: "absolute",
    top: 15,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    zIndex: 1
  },
  bottomLoadButton: {
    position: "absolute",
    bottom: 15,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    zIndex: 1
  },
  dashboardButtonContainer: {
    display: "flex",
    flexDirection: "column",
    zIndex: 1
  },
  cropButtonContainer: {
    display: "flex",
    flexDirection: "column",
    zIndex: 1
  },
  cropHighlightArea: {
    border: `2px solid ${theme.palette.secondary.light}`,
    position: "fixed"
  }
}));

interface Props {
  filePath?: string;
  fileKey?: string;
  displayFromSource?: boolean;
  sourceUrl?: string;
  page?: number;
  openFullScreen?: boolean;
  onOpenInFullScreen?: () => void;
  allowPartialLoad?: boolean;
  showZoomInOut?: boolean;
  onSetDashboardDeck?: ({ fileKey, page }: { fileKey: string, page?: number }) => Promise<void>;
  loadFromCacheBucket?: boolean;
  placeholder?: string;
  onLoaded?: () => void;
}


//TODO consider moving this up to a different level
const getCacheBucketName = (exports: any) => {
  return exports.aws_cache_bucket
}


const PAGE_WINDOW = 3
const getPageForSnippet = (pageNumber: number) => pageNumber < PAGE_WINDOW ? pageNumber : PAGE_WINDOW


const FilePreview = ({
  fileKey, page, filePath, allowPartialLoad = false, displayFromSource = false, placeholder = "Searching...", 
  sourceUrl, openFullScreen = false,  showZoomInOut = true, loadFromCacheBucket = false,
  onLoaded, onOpenInFullScreen,  onSetDashboardDeck }: Props) => {

  const classes = useStyles();
  const [fileUrl, setFileUrl] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [currentGlobalPage, setCurrentGlobalPage] = useState(0);
  const [pageNavigationPluginInstance] = useState(pageNavigationPlugin());
  const [cachedPdf, setCachedPdf] = useState<Uint8Array | undefined>()
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | undefined>()
  const [pinningDeckInProgress, setPinningDeckInProgress] = useState<boolean>(false);

  useEffect(() => {
    if (page) {
      setCurrentPage(page);
      setCurrentGlobalPage(page);
    }
    setCachedPdf(undefined)
    setPdfBytes(undefined)
  }, [fileKey, page, allowPartialLoad]);

  const zoomPluginInstance = zoomPlugin();
  const { ZoomIn, ZoomOut } = zoomPluginInstance;

  const viewerPlugins: any[] = [pageNavigationPluginInstance, zoomPluginInstance];

  //TODO consider putting this logic outside this component.This is an implementation detail
  //that we are stuffing into here with all possible options rather than having the calling
  //program/component provide the implementation
  useEffect(() => {
    setFileUrl('');
    if (fileKey && loadFromCacheBucket) {
      const cacheBucket = getCacheBucketName(awsConfig);
      s3DownloadExternalBucket({ key: fileKey, bucket: cacheBucket }).then(setPdfBytes);
    } else if (page && fileKey) { //get the snippet rather than the whole pdf
      const cacheBucket = getCacheBucketName(awsConfig);
      const pg = page + 1 //I'm still confused why we need to increment the page by one.  The page should already be incremented by one, see the process lambda
      const pageFileKey = join(dirname(fileKey), `${basename(fileKey, ".pdf")}_${pg}.pdf`)
      Promise.all([
        s3DownloadExternalBucket({ key: pageFileKey, bucket: cacheBucket }).then(setPdfBytes),
        s3Download({ key: fileKey }).then(setCachedPdf) //download full pdf while displaying snippet.  Should I just always download?  May be quicker than getting the url and then downloading
      ])
    }
    else if (fileKey && !displayFromSource) {
      console.log([fileKey]);
      s3Download({ key: fileKey }).then(setPdfBytes)
        .catch(() => {
          fetch(fileKey).then(data => {
            data.arrayBuffer().then(r => setPdfBytes(new Uint8Array(r)))
          });
        });
    } else if (sourceUrl) {
      setFileUrl(sourceUrl);
    }

  }, [fileKey, page, sourceUrl, displayFromSource, loadFromCacheBucket]);

  useEffect(() => {
    if (page && pageNavigationPluginInstance) {
      pageNavigationPluginInstance.jumpToPage(page);
    }
  }, [page, pageNavigationPluginInstance]);

  const defaultScale = filePath?.includes('.xlsx') || filePath?.includes('.xls') ? 1 : SpecialZoomLevel.PageWidth;

  const renderError = (error: LoadError) => {
    console.log(error)
    let message = 'Error loading the document';
    return (
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          height: '100%',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            backgroundColor: '#e53e3e',
            borderRadius: '0.25rem',
            color: '#fff',
            padding: '0.5rem',
          }}
        >
          {message}
        </div>
      </div>
    );
  };

  const viewerPayload = displayFromSource ?
    fileUrl
    :
    fileUrl || (cachedPdf)
      ?
      cachedPdf
      :
      pdfBytes;
  const initialPage = page ? getPageForSnippet(page) : currentGlobalPage;

  const content = viewerPayload ? (
    <Viewer
      fileUrl={viewerPayload}
      defaultScale={defaultScale}
      initialPage={initialPage} //initialPage is zero based.  But search results are not zero based?
      plugins={viewerPlugins}
      onDocumentLoad={(e) => {
        if (onLoaded)
          onLoaded();
      }}
      onPageChange={({ currentPage: newCurrentPage }) => {
        if (newCurrentPage > 0) {
          setCurrentPage(newCurrentPage);
          if (currentPage) {
            const offset = getPageForSnippet(currentPage);
            const globalPage = currentPage + newCurrentPage - offset;
            setCurrentGlobalPage(globalPage);
          }
          else {
            setCurrentGlobalPage(newCurrentPage);
          }
        }
      }}
      renderError={renderError}
    />
  ) : (
    <CircularProgress />
  );

  const folderPathElements = filePath?.split("/");
  folderPathElements?.shift();

  const fileContainerHeight = (): string => {
    let pathHeight = 18;

    if (filePath?.includes(".xls")) {
      return `calc(90% - ${pathHeight}px)`;
    }

    return `100%`;
  }

  const setDashboardDeck = async (page?: number) => {
    setPinningDeckInProgress(true);
    onSetDashboardDeck && await onSetDashboardDeck({ fileKey: fileKey as string, page });
    setPinningDeckInProgress(false);
  };

  return (
    <Grid container direction="row" className={classes.container}>
      <Grid item xs={12} className={classes.filePreviewContainer}>
        <Box
          height={fileContainerHeight()}
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          {(fileKey || sourceUrl) ? content : <Typography>{placeholder}</Typography>}
          {
            (fileKey || sourceUrl) && <>
              <div className={classes.zoomButtonsContainer}>
                {
                  onSetDashboardDeck && <>
                    <Backdrop
                      sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                      open={pinningDeckInProgress}
                    >
                      <CircularProgress color="inherit" />
                    </Backdrop>
                    <Tooltip title="Pin File" placement="left">
                      <Fab onClick={() => { setDashboardDeck() }} className={classes.zoomButton}>
                        <PushPinIcon />
                      </Fab>
                    </Tooltip>
                    <Tooltip title="Pin Page" placement="left">
                      <Fab onClick={() => { setDashboardDeck(currentPage + 1) }} className={classes.zoomButton}>
                        <PushPinTwoToneIcon />
                      </Fab>
                    </Tooltip>
                  </>
                }
                {
                  showZoomInOut &&
                  <>
                    <ZoomIn>
                      {
                        (props: RenderZoomOutProps) => (
                          <Fab onClick={props.onClick} className={classes.zoomButton}>
                            <AddIcon />
                          </Fab>
                        )
                      }
                    </ZoomIn>
                    <ZoomOut>
                      {
                        (props: RenderZoomOutProps) => (
                          <Fab className={classNames(classes.zoomButton)} onClick={props.onClick}>
                            <RemoveIcon />
                          </Fab>
                        )
                      }
                    </ZoomOut>
                  </>
                }
              </div>
              {
                openFullScreen &&
                <IconButton
                  className={classes.openIcon}
                  onClick={() => {
                    if (onOpenInFullScreen) {
                      onOpenInFullScreen();
                    }
                  }}
                >
                  <OpenInFullIcon />
                </IconButton>
              }
            </>
          }
        </Box>
      </Grid>
    </Grid>
  );
};

export default FilePreview;
